'use client';

import {clsx} from '@gfiber/typesmith';
import {Progress} from '@base-ui/react/progress';
import type React from 'react';
import styles from './CircularProgress.module.scss';
import {SIZE_MAP} from './constants';
import {strokeWidth} from './utils';
import {forwardRef} from 'react';

export type Size = keyof typeof SIZE_MAP;

interface CircularProgressProps {
  /** Hides the progress bar. Adding / removing this prop will trigger an animation in or out. */
  closed?: boolean;
  /** Max value for determinate progress bars. */
  max?: number;
  /** The color of the progress indicator. Can be 'primary' or a custom color string. */
  color?: string;
  /** Min value for determinate progress bars. */
  min?: number;
  /** Value for determinate progress bars. If not provided, the component will be in an indeterminate state. */
  progress?: number;
  /** The size of the loader in pixels or a predefined size. */
  size?: Size | number;
  /** The label which will set an aria-label for accessibility. */
  label?: string;
}

export const CircularProgress = forwardRef<
  HTMLDivElement,
  CircularProgressProps
>(function CircularProgress(props, forwardedRef) {
  const {
    progress,
    max = 100,
    min = 0,
    size: sizeProp = 'small',
    color = 'primary',
    closed = false,
    label,
    ...other
  } = props;

  const isIndeterminate = progress === null || progress === undefined;

  const size = typeof sizeProp === 'string' ? SIZE_MAP[sizeProp] : sizeProp;

  const radius = (size - strokeWidth(size)) / 2;
  const circumference = 2 * Math.PI * radius;
  const currentDasharray = isIndeterminate ? undefined : circumference;
  const currentDashoffset = isIndeterminate
    ? undefined
    : circumference - (progress / max) * circumference;

  const isPredefinedColor = color === 'primary';

  return (
    <Progress.Root
      value={isIndeterminate ? null : progress}
      max={max}
      min={min}
      aria-label={label}
      className={clsx(styles.root, {[styles.closed]: closed})}
      {...other}
    >
      <div
        ref={forwardedRef}
        className={clsx(styles.container, {
          [styles.indeterminateContainer]: isIndeterminate,
        })}
        style={{
          width: size,
          height: size,
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={styles.svgElement}
          aria-hidden="true"
          focusable="false"
        >
          {/* Track Circle */}
          <circle
            className={styles.trackCircle}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth(size)}
          />
          {/* Indicator Circle */}
          <circle
            className={clsx(
              styles.indicatorCircle,
              {[styles.indeterminateIndicator]: isIndeterminate},
              isPredefinedColor && styles[color],
            )}
            stroke={isPredefinedColor ? undefined : color}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth(size)}
            strokeDasharray={currentDasharray}
            strokeDashoffset={currentDashoffset}
            style={
              {
                '--circumference': circumference,
                '--dash-min': '1',
                '--dash-max': `${circumference * 0.5}`,
                '--off-mid': `${-circumference * 0.25}`,
                '--off-end': `${-circumference * 0.95}`,
              } as React.CSSProperties
            }
          />
        </svg>
        {!isIndeterminate && (
          <div
            className={styles.percentage}
            style={{
              fontSize: Math.max(8, size * 0.3),
            }}
          >
            {Math.round((progress / max) * 100)}%
          </div>
        )}
      </div>
    </Progress.Root>
  );
});
